<template>
  <div v-if="workOrder">
    <header id="page-header">
      <div class="content content-header pt-3">
        <h3 class="my-0">
          <b-button
            class="pl-0 pr-2 font-w700 font-size-lg d-md-none"
            variant="link"
            @click="$store.commit('setSidebarCollapsed', !$store.state.layout.sidebarCollapsed)"
          >
            <i class="fa fa-bars" />
          </b-button>
        </h3>
        <h3 class="my-0">
          <b-button
            @click="() => $router.go(-1)"
            class="pl-0 pr-2 font-w700 font-size-lg d-none d-md-block"
            variant="link"
          >
            <i class="fa fa-arrow-left" />
          </b-button>
        </h3>

        <div class="d-lg-flex flex-grow-1 align-items-center gap-2 overflow-hidden">
          <h1 class="h3 my-0 text-dark font-w600">
            {{ workOrder.number }}
          </h1>

          <status-badge
            :status="workOrder.status"
            :status-variants="statusVariants"
          />
        </div>

        <div class="d-flex flex-wrap align-items-center justify-content-end gap-2 flex-shrink-0">
          <b-button
            v-if="$can('reassign', workOrder)"
            variant="primary"
            v-b-modal.technician-work-order-reassign-modal
            class="d-none d-md-inline-block"
          >
            Reassign
          </b-button>

          <b-dropdown
            variant="outline-secondary"
            no-caret
            right
            class="d-md-none"
          >
            <template #button-content>
              <i class="fa fa-ellipsis-v" />
            </template>
            <b-dropdown-item
              v-if="$can('reassign', workOrder)"
              v-b-modal.technician-work-order-reassign-modal
            >
              Reassign
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <div class="content pt-0 pb-3">
        <div class="d-flex flex-wrap align-items-center text-muted font-size-sm">
          <span
            class="d-inline-flex align-items-center"
            v-b-tooltip.hover
            :title="locationTooltip"
          >
            <i class="fa fa-map-marker-alt mr-2 text-primary" />
            <span class="text-secondary">
              Store #{{ workOrder.location.store_number }}
              <span v-if="workOrder.location.name && workOrder.location.name != workOrder.location.store_number">
                - {{ workOrder.location.name }}
              </span>
              <span v-if="locationCityState"> &middot; {{ locationCityState }}</span>
            </span>
          </span>
          <span class="text-muted mx-2">|</span>
          <span class="d-inline-flex align-items-center">
            <i class="fa fa-building mr-2 text-primary" />
            <span class="text-secondary">
              {{ workOrder.client.name }}
            </span>
          </span>
        </div>
      </div>

      <div class="content border-top border-gray py-3">
        <b-row>
          <b-col
            cols="6"
            md
            class="mb-3 mb-md-0"
          >
            <div class="text-muted font-w500 font-size-sm mb-1">
              Date Bucket
            </div>
            <div
              v-if="workOrder.date_bucket_start"
              class="font-w600"
            >
              {{ workOrder | dateRangeFormat("date_bucket_start", "date_bucket_end", workOrder.location.time_zone) }}
            </div>
            <div
              v-else
              class="text-muted"
            >
              -
            </div>
          </b-col>

          <b-col
            cols="6"
            md
            class="mb-3 mb-md-0"
          >
            <div class="text-muted font-w500 font-size-sm mb-1">
              Priority
            </div>
            <div
              v-if="workOrder.priority"
              class="font-w600"
            >
              {{ workOrder.priority }}
            </div>
            <div
              v-else
              class="text-muted"
            >
              -
            </div>
          </b-col>

          <b-col
            v-if="isCompleted"
            cols="6"
            md
            class="mb-3 mb-md-0"
          >
            <div class="text-muted font-w500 font-size-sm mb-1">
              Work Completed
            </div>
            <div class="font-w600">
              <div>
                {{ workOrder | dateRangeFormat("checked_in_at", "checked_out_at", workOrder.location.time_zone) }}
              </div>
              <div class="font-size-sm font-w400">
                {{ workOrder | timeRangeFormat("checked_in_at", "checked_out_at", workOrder.location.time_zone) }}
              </div>
            </div>
          </b-col>

          <b-col
            v-if="showPayment"
            cols="6"
            md
            class="mb-3 mb-md-0"
          >
            <div class="text-muted font-w500 font-size-sm mb-1">
              Payment Status
            </div>
            <status-badge
              :status="workOrder.payment_status"
              :status-variants="paymentStatusVariants"
            />
          </b-col>

          <b-col
            v-if="showPayment"
            cols="6"
            md
          >
            <div class="text-muted font-w500 font-size-sm mb-1">
              Amount
            </div>
            <div class="font-w600">
              <money-format
                :value="workOrder.cost.amount"
                :currency-code="workOrder.cost.currency"
                subunits-value
              />
            </div>
          </b-col>
        </b-row>
      </div>
    </header>

    <div class="content">
      <b-tabs
        content-class="pt-4 mb-3"
        v-model="activeTab"
        nav-class="wo-show-tabs"
      >
        <b-tab
          active
          title="Details"
        >
          <b-row>
            <b-col
              cols="12"
              lg="8"
            >
              <div
                v-if="workOrder.description"
                class="mb-4"
              >
                <div class="text-muted font-w500 font-size-sm mb-1">
                  Description
                </div>
                <div class="text-prewrap">
                  {{ workOrder.description }}
                </div>
              </div>

              <b-row>
                <b-col
                  v-if="workOrder.cancellation_reason"
                  cols="12"
                  sm="6"
                  class="mb-3"
                >
                  <div class="text-muted font-w500 font-size-sm mb-1">
                    Cancellation Reason
                  </div>
                  <div>
                    {{ workOrder.cancellation_reason | humanizedFormat | capitalizeFormat }}
                  </div>
                </b-col>

                <b-col
                  cols="12"
                  sm="6"
                  class="mb-3"
                >
                  <div class="text-muted font-w500 font-size-sm mb-1">
                    Maintenance Type
                  </div>
                  <div>{{ workOrder.maintenance_type || '-' }}</div>
                </b-col>

                <b-col
                  v-if="workOrder.completion_source"
                  cols="12"
                  sm="6"
                  class="mb-3"
                >
                  <div class="text-muted font-w500 font-size-sm mb-1">
                    Completion Source
                  </div>
                  <div>
                    {{ workOrder.completion_source | humanizedFormat | capitalizeFormat }}
                  </div>
                </b-col>
              </b-row>

              <div class="mt-2">
                <hr>
                <h5 class="font-w500 mb-3">
                  Technicians
                </h5>
                <b-row>
                  <b-col
                    cols="12"
                    sm="6"
                    class="mb-3"
                  >
                    <div class="text-muted font-w500 font-size-sm mb-1">
                      Completed By
                    </div>
                    <div v-if="workOrder.done_by">
                      <b-link :to="{ name: 'technician-user', params: { id: workOrder.done_by.id } }">
                        {{ workOrder.done_by.name }}
                      </b-link>
                    </div>
                    <div
                      v-else
                      class="text-muted"
                    >
                      -
                    </div>
                  </b-col>
                </b-row>
              </div>

              <div class="mt-3 text-muted font-size-sm">
                <div>Last updated {{ workOrder.updated_at | dateTimeFormat }}</div>
                <div>Created on {{ workOrder.created_at | dateTimeFormat }}</div>
              </div>
            </b-col>

            <b-col
              cols="12"
              class="d-lg-none"
            >
              <hr class="my-0">
            </b-col>

            <b-col
              cols="12"
              lg="4"
              class="pl-lg-4 border-left border-gray"
            >
              <div class="pb-4">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <h5 class="font-w500 my-0">
                    Location
                  </h5>
                  <b-link
                    v-if="mapUrl"
                    :href="mapUrl"
                    target="_blank"
                    rel="noopener"
                  >
                    Open Map
                  </b-link>
                </div>

                <div class="row">
                  <div class="col-6 text-muted font-w500 font-size-sm">
                    Address
                  </div>
                  <div class="col-6 text-right font-size-sm mb-3">
                    <div>{{ workOrder.location.street }}</div>
                    <div>
                      {{
                        {
                          city: workOrder.location.city,
                          state: workOrder.location.state,
                          zip_code: workOrder.location.zip_code,
                        } | locationFormat
                      }}
                    </div>
                  </div>
                </div>

                <map-with-markers
                  :height="200"
                  :marker-distance="workOrder.check_in_distance"
                  :markers="mapMarkers"
                />
              </div>

              <div
                v-if="showPayment"
                class="pt-2 border-top border-gray"
              >
                <h5 class="font-w500 mb-3">
                  Payment
                </h5>

                <div
                  v-if="technicianPayments.length > 0"
                  class="d-flex justify-content-between py-2"
                >
                  <span class="text-muted font-w500 font-size-sm">Date</span>
                  <span>
                    <b-link
                      v-for="(technicianPayment, index) in technicianPayments"
                      :key="index"
                      class="ml-1"
                      :to="{ name: 'technician-user-payment', params: { id: technicianPayment.id } }"
                    >
                      {{ technicianPayment.due_date | dateFormat }}
                    </b-link>
                  </span>
                </div>

                <div class="d-flex justify-content-between py-2">
                  <span class="text-muted font-w500 font-size-sm">Status</span>
                  <status-badge
                    :status="workOrder.payment_status"
                    :status-variants="paymentStatusVariants"
                  />
                </div>

                <div class="d-flex justify-content-between py-2">
                  <span class="text-muted font-w500 font-size-sm">Amount</span>
                  <money-format
                    :value="workOrder.cost.amount"
                    :currency-code="workOrder.cost.currency"
                    subunits-value
                  />
                </div>
              </div>
            </b-col>
          </b-row>

          <div
            v-if="hasSignature"
            class="mt-2"
          >
            <hr>
            <h5 class="font-w500 mb-3">
              Manager Review
            </h5>
            <b-row>
              <b-col
                md="7"
                class="border-right"
              >
                <div class="pb-3 pt-0">
                  <b-row>
                    <b-col
                      cols="auto"
                      class="mr-auto text-muted font-w500 font-size-sm"
                    >
                      Manager Name
                    </b-col>
                    <b-col cols="auto">
                      {{ workOrder.manager_review?.name }}
                    </b-col>
                  </b-row>
                </div>

                <div class="pb-3">
                  <b-row class="mb-2">
                    <b-col
                      cols="auto"
                      class="mr-auto text-muted font-w500 font-size-sm"
                    >
                      Manager Comment
                    </b-col>
                    <b-col cols="auto">
                      {{ workOrder.manager_review?.comments }}
                    </b-col>
                  </b-row>
                </div>

                <b-row>
                  <b-col
                    cols="auto"
                    class="mr-auto text-muted font-w500 font-size-sm"
                  >
                    Rating
                  </b-col>

                  <b-col cols="auto">
                    <star-rating
                      :rating="workOrder.manager_review?.rating"
                      :star-size="36"
                      :show-rating="false"
                      read-only
                      active-color="#084298"
                    />
                  </b-col>
                </b-row>
              </b-col>
              <b-col md="5">
                <b-row class="h-100">
                  <b-col
                    cols="auto"
                    class="mr-auto"
                  >
                    <img
                      v-if="signatureUrl"
                      class="img-fluid img-signature"
                      :src="signatureUrl"
                    >
                    <p v-else>
                      Signature not found.
                    </p>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </div>
        </b-tab>

        <b-tab>
          <template #title>
            <number-badge
              title="Photos"
              :total="photosTotal"
            />
          </template>
          <photos-panel
            :work-order="workOrder"
            @on-save="getWorkOrder"
            @on-destroy="getWorkOrder"
          />
        </b-tab>
      </b-tabs>
    </div>

    <reassign-modal
      :value="workOrder"
      @input="onWorkOrderChanged"
    />
  </div>
</template>

<script>
import StarRating from 'vue-star-rating';
import StatusBadge from '@/components/shared/StatusBadge.vue';
import PhotosPanel from '@/components/technician/workOrders/PhotosPanel.vue';
import ReassignModal from '@/components/technician/workOrders/ReassignModal.vue';
import NumberBadge from '@/components/shared/NumberBadge.vue';
import MapWithMarkers from '@/components/shared/MapWithMarkers.vue';
import { isNil as _isNil } from 'lodash';
import { TECHNICIAN_STATUS_VARIANTS, TECHNICIAN_WORK_ORDER_STATUS_COMPLETED } from '@/constants/workOrders';
import { STATUS_VARIANTS } from '@/constants/technicianPayments';

export default {
  name: 'TechnicianWorkOrdersShow',
  components: {
    StarRating,
    StatusBadge,
    PhotosPanel,
    ReassignModal,
    NumberBadge,
    MapWithMarkers,
  },
  data() {
    return {
      workOrder: null,
      statusVariants: TECHNICIAN_STATUS_VARIANTS,
      paymentStatusVariants: STATUS_VARIANTS,
      technicianPayments: [],
      activeTab: 0,
    };
  },
  mounted() {
    this.getWorkOrder();
    this.getTechnicianPayments();
  },
  methods: {
    getWorkOrder() {
      this.$technicianWorkOrdersAPI
        .get(this.$route.params.id)
        .then((workOrder) => {
          this.workOrder = workOrder;
        })
        .catch(() => {
          this.$router.push('/404');
        });
    },
    getTechnicianPayments() {
      this.$technicianUserPaymentsAPI.getTechnicianPayments({ work_order_id: this.$route.params.id }).then((response) => {
        this.technicianPayments = response.technicianPayments;
      });
    },
    onWorkOrderChanged(workOrder) {
      this.workOrder = workOrder;
    },
  },
  computed: {
    user() {
      return this.$store.getters.user;
    },
    showPayment() {
      return this.user?.id === this.workOrder?.assigned_to?.id;
    },
    isCompleted() {
      return this.workOrder?.status === TECHNICIAN_WORK_ORDER_STATUS_COMPLETED
        || !!(this.workOrder?.checked_in_at && this.workOrder?.checked_out_at);
    },
    hasSignature() {
      return !_isNil(this.workOrder.manager_review);
    },
    signatureUrl() {
      return this?.workOrder?.manager_review?.signature?.url;
    },
    locationCityState() {
      return this.$options.filters.locationFormat({
        city: this.workOrder.location.city,
        state: this.workOrder.location.state,
      });
    },
    locationTooltip() {
      const { street, city, state, county, zip_code } = this.workOrder.location;
      return `${street}, ${city}, ${state}, ${county}, ${zip_code}`;
    },
    mapUrl() {
      const { latitude, longitude } = this.workOrder.location;
      if (!latitude || !longitude) {
        return null;
      }
      return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    },
    mapMarkers() {
      return [
        {
          coord: {
            lat: parseFloat(this.workOrder.location.latitude),
            lng: parseFloat(this.workOrder.location.longitude),
          },
          label: 'Work Order Location',
        },
        {
          coord: {
            lat: parseFloat(this.workOrder.latitude),
            lng: parseFloat(this.workOrder.longitude),
          },
          label: 'Check in Location',
        },
      ];
    },
    photosTotal() {
      if (!this.workOrder) { return 0; }
      return (this.workOrder.before_photos || []).length
        + (this.workOrder.after_photos || []).length
        + (this.workOrder.no_show_photos || []).length;
    },
  },
};
</script>

<style scoped>
.img-signature {
  max-height: 100px;
}
::v-deep .wo-show-tabs {
  border-bottom: 1px solid #e9ecef;
}
</style>
